/* eslint-disable @eslint-react/dom/no-missing-iframe-sandbox */
import { useIsVisible } from '@folklore/hooks';
import { loadTikTok, loadInstagram, loadTwitter, loadScript } from '@folklore/services';
import classNames from 'classnames';
import { useRef, useEffect } from 'react';

import { useIsCapture } from '../../contexts/LayoutContext';

import styles from '../../styles/blocks/embed-block.module.css';

export interface EmbedProps {
    embed?: Embed | null;
    loading?: string;
    className?: string | null;
}

function Embed({ embed = null, loading = 'lazy', className = null }: EmbedProps) {
    const refEmbed = useRef(null);
    const { provider, iframeUrl = null, html = null, width, height } = embed || {};
    const finalFrameUrl = iframeUrl || null;
    const iframeContainerRef = useRef(null);
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref: visibleRef, visible: isVisible } = useIsVisible({
        rootMargin: '200px',
        persist: true,
        disabled: !isLazyLoading,
    });

    const shouldLoad = !isLazyLoading || isVisible || provider === 'instagram';
    // const shouldLoad = true;

    useEffect(() => {
        const { current: element = null } = iframeContainerRef;
        if (provider === 'twitter' && shouldLoad) {
            loadTwitter().then((twttr) => {
                if (element !== null) {
                    twttr.widgets.load(element);
                }
            });
        } else if (provider === 'instagram' && shouldLoad) {
            if (window.FB) {
                window.FB.__buffer = true;
            }
            loadInstagram().then(() => {
                if (typeof window.instgrm !== 'undefined') {
                    window.instgrm.Embeds.process();
                }
            });
        } else if (provider === 'tiktok' && shouldLoad) {
            loadTikTok();
        } else if (provider === 'donorbox' && shouldLoad) {
            loadScript('https://donorbox.org/widget.js');
        }
    }, [provider, shouldLoad]);

    return (
        <div
            ref={visibleRef}
            className={classNames([styles.container, styles[provider], className])}
        >
            {finalFrameUrl !== null ? (
                <div className={styles.iframeContainer} ref={iframeContainerRef}>
                    {shouldLoad ? (
                        <iframe
                            src={finalFrameUrl}
                            ref={refEmbed}
                            className={classNames([styles.iframe, `iframe-${provider}`])}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; payment"
                            allowFullScreen
                            title="Embed"
                            width={width || '100%'}
                            height={height || '100%'}
                            {...(provider === 'donorbox'
                                ? {
                                      name: 'donorbox',
                                      allowPaymentRequest: 'allowpaymentrequest',
                                      seamless: 'seamless',
                                      scrolling: 'no',
                                  }
                                : null)}
                        />
                    ) : null}
                </div>
            ) : null}
            {finalFrameUrl === null && html !== null ? (
                <div
                    className={classNames([styles.iframeContainer])}
                    dangerouslySetInnerHTML={{ __html: html }}
                    ref={iframeContainerRef}
                />
            ) : null}
        </div>
    );
}

export default Embed;
